import { Calculator, ClipboardCheck, Headset, ShieldCheck, Truck, Users, Wrench, type LucideIcon } from "lucide-react";

export interface PeopleGroup {
  icon: LucideIcon;
  title: string;
  kicker: string;
  body: string;
  image: string;
  imageAlt: string;
  photoDirection: string;
}

export interface PublicTeamProfile {
  name: string;
  title: string;
  department: string;
  bio: string;
  helpsWith: string[];
  image: string;
  imageAlt: string;
}

export const publicTeamProfiles: PublicTeamProfile[] = [
  {
    name: "Leadership",
    title: "Owner / operations lead",
    department: "Leadership",
    bio: "Company leadership sets the operating standard, supports customer relationships, and keeps the team focused on dependable tanker freight.",
    helpsWith: ["Company direction", "Customer relationships", "Operations standards"],
    image: "/brand/industries/chemicals.jpg",
    imageAlt: "Leadership profile placeholder for Bull and Bear Co",
  },
  {
    name: "Dispatch",
    title: "Dispatch / operations",
    department: "Operations",
    bio: "Dispatch and operations coordinate load details, driver communication, status updates, and the daily follow-through behind each shipment.",
    helpsWith: ["Load communication", "Driver coordination", "Status updates"],
    image: "/brand/blog/shipment-tracking.jpg",
    imageAlt: "Dispatch profile placeholder for Bull and Bear Co",
  },
  {
    name: "Safety",
    title: "Safety / compliance",
    department: "Safety",
    bio: "Safety and compliance support driver files, document reviews, qualification checks, and the standards required for responsible tanker work.",
    helpsWith: ["Driver files", "Compliance reviews", "Document follow-up"],
    image: "/brand/blog/choosing-tanker-carrier.jpg",
    imageAlt: "Safety profile placeholder for Bull and Bear Co",
  },
  {
    name: "Accounting",
    title: "Accounting / billing",
    department: "Accounting",
    bio: "Accounting supports billing, settlements, document follow-up, payment questions, and the back-office details that keep work moving.",
    helpsWith: ["Billing support", "Settlements", "Payment paperwork"],
    image: "/brand/industries/fuel-energy.jpg",
    imageAlt: "Accounting profile placeholder for Bull and Bear Co",
  },
  {
    name: "Maintenance",
    title: "Maintenance / shop",
    department: "Maintenance",
    bio: "Maintenance and shop support help keep tractors, tankers, inspections, and repairs moving so equipment is ready for dispatch.",
    helpsWith: ["Equipment readiness", "Inspections", "Repair coordination"],
    image: "/brand/industries/manufacturing.jpg",
    imageAlt: "Maintenance shop profile placeholder for Bull and Bear Co",
  },
];

export const peopleGroups: PeopleGroup[] = [
  {
    icon: Headset,
    title: "Dispatch and office",
    kicker: "Planning, communication, follow-through",
    body: "The team planning loads, answering the phone, tracking details, and keeping paperwork moving between customers, brokers, and drivers.",
    image: "/brand/blog/shipment-tracking.jpg",
    imageAlt: "Dispatch and office team coordinating tanker freight",
    photoDirection: "Use a candid office or dispatch photo: people at desks, on calls, or reviewing load details.",
  },
  {
    icon: Truck,
    title: "Drivers and owner-operators",
    kicker: "The face of BB on the road",
    body: "The people representing Bull & Bear on the road, at loading racks, and at delivery sites every day.",
    image: "/brand/careers/drivers.jpg",
    imageAlt: "Bull and Bear driver with tanker equipment",
    photoDirection: "Use a real driver or owner-operator photo near a clean tractor/tanker, ideally outside the yard or at inspection.",
  },
  {
    icon: ShieldCheck,
    title: "Safety and compliance",
    kicker: "Documents, reviews, and standards",
    body: "The group watching applications, documents, driver qualifications, and review workflows so freight moves with the right controls.",
    image: "/brand/blog/choosing-tanker-carrier.jpg",
    imageAlt: "Safety and compliance paperwork review for tanker operations",
    photoDirection: "Use a staff photo reviewing paperwork, tablets, credentials, or safety checklists with no private data visible.",
  },
  {
    icon: Calculator,
    title: "Accounting",
    kicker: "Billing, settlements, payment paperwork",
    body: "The team supporting invoicing, settlements, payment paperwork, document follow-up, and the financial details that keep work moving cleanly.",
    image: "/brand/industries/fuel-energy.jpg",
    imageAlt: "Accounting and billing support for tanker freight operations",
    photoDirection: "Use a real accounting or office photo: billing review, settlement paperwork, or back-office support with private details hidden.",
  },
  {
    icon: Wrench,
    title: "Maintenance shop",
    kicker: "Equipment readiness",
    body: "Our shop team supports the operation by helping keep tractors, tankers, inspections, and repairs moving so equipment is ready for the next load.",
    image: "/brand/industries/manufacturing.jpg",
    imageAlt: "Maintenance shop support for tanker equipment readiness",
    photoDirection: "Use a real shop photo: technician, tools, inspection, tractor bay, or tanker readiness work.",
  },
];

export const peopleWorkflow = [
  {
    icon: Users,
    title: "One company, multiple teams",
    body: "Customers may see one truck or one dispatcher, but each load is supported by several teams working together.",
  },
  {
    icon: ClipboardCheck,
    title: "Paperwork and payment matter",
    body: "Applications, onboarding files, BOLs, PODs, insurance, W-9s, invoices, and settlements all need organized review.",
  },
  {
    icon: Wrench,
    title: "The shop supports the road",
    body: "Maintenance is part of the operation, not separate from it. The shop helps reduce downtime and keep equipment ready.",
  },
];
